/**
 * Gets customer details from state, sets up occConfig
 * Loads SimplifiedCardModule component to show customer details
 */
import { useOktaAuth } from '@okta/okta-react';
import type { FC } from 'react';
import { Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';

import * as bridgeUserSelectors from '@makemydeal/dr-dash-store/selectors/bridgeUserSelectors';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { getDealXgId, getDealerMMDId } from '@makemydeal/dr-sales-checkin-store';
import type { CustomerDetailsProps } from '@makemydeal/dr-sales-checkin-types';
import { ConversationTypeEnum, CUSTOMER_REQUIRED_FIELDS_FOR_PBC } from '@makemydeal/dr-sales-checkin-types';
import { RemoteModuleErrorBoundary } from './RemoteModuleErrorBoundary';
import { SimplifiedCardModule } from './SimplifiedCardModule';
import SalesCheckInSpinner from '../SalesCheckInSpinner/SalesCheckInSpinner';

export const CustomerDetails: FC<CustomerDetailsProps> = ({
    commonConsumerId,
    onCustomerChanged,
    messageContentSelector,
    enableReadOnlyEditFields,
    defaultTab = 'inbox'
}) => {
    const defaultMessageContent = useSelector(messageContentSelector ?? (() => ''));
    const username = useSelector(bridgeUserSelectors.getBridgeUsername);
    const fullName = useSelector(bridgeUserSelectors.getFullName);
    const nameShown = fullName.length ? fullName : username;
    const environment = offerSessionUtils.getEnvironmentType();
    const oktaContext = useOktaAuth();
    const preloaderEnabled = useSelector(featureToggleSelectors.isSciCustomerCardPreloaderEnabled);
    const dealId = useSelector(getDealXgId);
    const dealerId = useSelector(getDealerMMDId);

    useEffect(() => {
        logNewRelicPageAction('SCI:CustomerDetails - Open', {
            preloaderEnabled,
            hideCheckLoaderActive: preloaderEnabled,
            dealerId: String(dealerId),
            dealXgId: dealId
        });
    }, []);
    const getJwtToken = () => oktaContext?.oktaAuth.getAccessToken() || '';

    // Disable OCC inbox for business customers
    // Set occConfig.defaultTab to ConversationTypeEnum.TEXT if defaultTab is 'inbox', else ConversationTypeEnum.EMAIL
    const occConfig = {
        username: nameShown,
        messageContent: defaultMessageContent,
        defaultTab: ConversationTypeEnum.TEXT
    };

    return (
        <RemoteModuleErrorBoundary moduleName="SimplifiedCardModule" commonConsumerId={commonConsumerId}>
            <Suspense fallback={<SalesCheckInSpinner />}>
                <SimplifiedCardModule
                    commonConsumerID={commonConsumerId}
                    viewportMode="sidebar"
                    environment={environment}
                    getAuthOverride={getJwtToken}
                    onCustomerEdited={onCustomerChanged}
                    occConfig={occConfig}
                    editCapability="hidden"
                    enableReadOnlyEditFields={enableReadOnlyEditFields}
                    requiredFields={CUSTOMER_REQUIRED_FIELDS_FOR_PBC}
                    defaultTab={defaultTab}
                />
            </Suspense>
        </RemoteModuleErrorBoundary>
    );
};
